/**
 * @description Die Impressums-Seite der Anwendung. Zeigt rechtliche Informationen und
 * Kontaktmöglichkeiten an.
 */

import { useEffect } from "react";
import { SearchFocusTarget, User } from "@/App";
import { useApp } from "@/contexts/AppContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Building2,
  MapPin,
  AlertTriangle,
  PenTool,
} from "lucide-react";

interface LegalNoticeProps {
  navigate: (page: string) => void;
  currentUser: User | null;
  onLogout: () => void;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
}

export function LegalNotice({
  navigate,
  currentUser,
  onLogout,
  onNavigateToSearchPage,
}: LegalNoticeProps) {
  const { t } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "var(--foreground)",
  } as const;
  const secondaryTextStyle = {
    color: "var(--foreground)",
  } as const;
  const warningPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--accent) 12%, var(--background))",
    borderLeftColor:
      "color-mix(in srgb, var(--accent) 70%, var(--background))",
  } as const;
  const warningIconStyle = {
    color: "color-mix(in srgb, var(--accent) 85%, var(--foreground))",
  } as const;
  const normalizeMultilineText = (value: string): string =>
    value.replace(/\\r\\n/g, "\n").replace(/\\n/g, "\n");
  const normalizedUniversity = normalizeMultilineText(
    t("legalNotice.university")
  );
  const normalizedEditorAddress = normalizeMultilineText(
    t("legalNotice.editorAddress")
  );
  const rawContactAddress = normalizeMultilineText(t("legalNotice.address"));
  const formattedContactAddress = (rawContactAddress.includes("\n")
    ? rawContactAddress.split(/\r?\n/g)
    : rawContactAddress.split(",")
  )
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join("\n");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: "var(--background)" }}
    >
      <Header
        navigate={navigate}
        currentUser={currentUser}
        onLogout={onLogout}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />

      <main className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="mb-8">
            <h2 className="text-primary-heading mb-2">
              {t("legalNotice.title")}
            </h2>
            <p className="text-secondary" style={secondaryTextStyle}>
              {t("legalNotice.subtitle")}
            </p>
          </div>

          <div className="space-y-6">
            <div
              className="border-l-4 p-4 rounded-r-md shadow-sm"
              style={warningPanelStyle}
            >
              <div className="flex items-start">
                <AlertTriangle
                  className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0"
                  style={warningIconStyle}
                />
                <div>
                  <h3 className="text-sm font-semibold" style={primaryTextStyle}>
                    {t("legalNotice.projectDisclaimerTitle")}
                  </h3>
                  <p className="text-sm mt-1" style={secondaryTextStyle}>
                    {t("legalNotice.projectDisclaimerText")}
                  </p>
                </div>
              </div>
            </div>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Building2 className="w-5 h-5" style={iconStyle} />
                  {t("legalNotice.projectTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-body" style={primaryTextStyle}>
                <p>
                  <strong>{t("legalNotice.projectGroup")}</strong>
                  <br />
                  <span style={{ whiteSpace: "pre-line" }}>
                    {normalizedUniversity}
                  </span>
                </p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold text-base">
                  <PenTool className="w-4 h-4" style={iconStyle} />
                  {t("legalNotice.editorTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-body" style={primaryTextStyle}>
                <p>
                  <strong>{t("legalNotice.editorName")}</strong>
                  <br />
                  <span style={{ whiteSpace: "pre-line" }}>
                    {normalizedEditorAddress}
                  </span>
                </p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <MapPin className="w-5 h-5" style={iconStyle} />
                  {t("legalNotice.contactTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-body" style={primaryTextStyle}>
                <p style={{ whiteSpace: "pre-line" }}>{formattedContactAddress}</p>
                <p className="mt-4 space-y-1 flex flex-col">
                  <a
                    href={`tel:${t("legalNotice.phone")}`}
                    className="text-[var(--primary)] hover:underline"
                  >
                    {t("legalNotice.phone")}
                  </a>
                  <a
                    href={`mailto:${t("legalNotice.email")}`}
                    className="text-[var(--primary)] hover:underline"
                  >
                    {t("legalNotice.email")}
                  </a>
                  <a
                    href={t("legalNotice.website")}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[var(--primary)] hover:underline"
                  >
                    {t("legalNotice.website")}
                  </a>
                </p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="text-primary-heading font-semibold">
                  {t("legalNotice.disclaimer")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 text-body" style={primaryTextStyle}>
                <div>
                  <h4 className="font-semibold mb-2" style={primaryTextStyle}>
                    {t("legalNotice.disclaimerContent")}
                  </h4>
                  <p className="text-sm leading-relaxed">
                    {t("legalNotice.disclaimerContentText")}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2" style={primaryTextStyle}>
                    {t("legalNotice.disclaimerLinks")}
                  </h4>
                  <p className="text-sm leading-relaxed">
                    {t("legalNotice.disclaimerLinksText")}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2" style={primaryTextStyle}>
                    {t("legalNotice.disclaimerCopyright")}
                  </h4>
                  <p className="text-sm leading-relaxed">
                    {t("legalNotice.disclaimerCopyrightText")}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer navigate={navigate} />
    </div>
  );
}




